import { Component, OnInit, OnDestroy, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatMenuModule } from '@angular/material/menu';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSort, MatSortModule } from '@angular/material/sort';
import { MatTableModule } from '@angular/material/table';
import { DataSourceOrdenacaoInsensivel } from '../../../../shared/helpers/ordenacao-tabela.helper';
import { RouterModule } from '@angular/router';
import { SelectionModel } from '@angular/cdk/collections';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { ConfirmModalComponent } from '../../../../shared/components/confirm-modal/confirm-modal.component';
import { EmptyListComponent } from '../../../../shared/components/empty-list/empty-list.component';
import { ModuloEnum } from '../../../../shared/enums/usuario/modulo.enum';
import { FuncionalidadeEnum } from '../../../../shared/enums/usuario/funcionalidade.enum';
import { AlertService, AlertType } from '../../../../shared/services/alert.service';
import { CentroCustoService } from '../../../../shared/services/apis/financas/centro-custo.service';
import { CentroCusto } from '../../../../shared/services/models/financas/centro-custo.model';
import { HeaderComponent } from '../../../../shared/components/header/header.component';
import { LoadingBoxDefaultListComponent } from '../../../../shared/components/loading-box-default-list/loading-box-default-list.component';
import { UtilService } from '../../../../shared/services/util.service';
import { DeleteCentroCustoResponse } from '../../../../shared/services/responses/financas/delete-centro-custo.response';
import { BulkDeleteCentroCustoResponse } from '../../../../shared/services/responses/financas/bulk-delete-centro-custo.response';
import { StatusExclusaoCentroCustoEnum } from '../../../../shared/enums/financas/status-exclusao-centro-custo.enum';

@Component({
    selector: 'app-centros-custo',
    standalone: true,
    imports: [
        CommonModule, ReactiveFormsModule, FormsModule, MatFormFieldModule,
        MatInputModule, MatButtonModule, MatIconModule, MatTableModule,
        MatCheckboxModule, MatMenuModule, MatSortModule, MatProgressSpinnerModule,
        MatDialogModule, EmptyListComponent, RouterModule, HeaderComponent,
        LoadingBoxDefaultListComponent
    ],
    templateUrl: './centros-custo.component.html',
    styleUrl: './centros-custo.component.css'
})
export class CentrosCustoComponent implements OnInit, OnDestroy {
    public moduloEnum = ModuloEnum.Configuracoes;
    public funcionalidadeEnum = FuncionalidadeEnum.Configuracoes_Financas;

    public model: FormGroup;
    public _paginaAtual: number = 0;
    public _tamanhoPagina: number = 20;
    public _totalRegistros: number = 0;

    public _dataSource = new DataSourceOrdenacaoInsensivel<CentroCusto>();
    public _isLoading: boolean = false;

    public displayedColumns: string[] = ['select', 'codigo', 'nome', 'status', 'actions'];
    public selection = new SelectionModel<CentroCusto>(true, []);

    @ViewChild(MatSort) set matSort(sort: MatSort) {
        this._dataSource.sort = sort;
    }

    constructor(
        private formBuilder: FormBuilder,
        private centroCustoService: CentroCustoService,
        private alertService: AlertService,
        private dialog: MatDialog,
        private utilService: UtilService
    ) {
        this._dataSource.sortingDataAccessor = (item, property) => {
            if (property === 'status') return item.isAtivo ? 1 : 0;
            const value = (item as any)[property];
            return typeof value === 'string' ? value.toLowerCase() : value;
        };
    }

    ngOnInit(): void {
        this.model = this.formBuilder.group({ pesquisa: [''] });
        this.loadFilters();
        this.carregarDados();
    }

    ngOnDestroy(): void {
        this.utilService.salvarFiltro(this.moduloEnum, this.funcionalidadeEnum, this.model,
            ['/configuracoes/financas/centros-custo/cadastrar']);
    }

    private loadFilters(): void {
        const filtroSalvo = this.utilService.obterFiltro(this.moduloEnum, this.funcionalidadeEnum);
        if (filtroSalvo) { this.model.patchValue(filtroSalvo); }
    }

    public carregarDados(reset: boolean = false): void {
        this._isLoading = true;
        const pesquisa = this.model.get('pesquisa')?.value || '';
        this.centroCustoService.getAll(pesquisa, this._paginaAtual, this._tamanhoPagina).subscribe({
            next: (response) => {
                this._isLoading = false;
                if (response.isError) {
                    this.alertService.show('Aviso', response.errorDescription || 'Erro ao carregar centros de custo', AlertType.warning);
                    return;
                }
                this._totalRegistros = response.totalRegistros;
                if (reset) {
                    this._dataSource.data = response.listaCentrosCusto;
                } else {
                    this._dataSource.data = [...this._dataSource.data, ...response.listaCentrosCusto];
                }
            },
            error: (err) => {
                this._isLoading = false;
                this.alertService.show('Aviso', err.message || 'Erro ao carregar centros de custo', AlertType.warning);
            }
        });
    }

    public proximaPagina(): void { this._paginaAtual++; this.carregarDados(); }

    public clickPesquisar(): void {
        this._paginaAtual = 0;
        this.selection.clear();
        this._dataSource.data = [];
        this.carregarDados(true);
    }

    public isAllSelected() {
        return this.selection.selected.length === this._dataSource.data.length && this._dataSource.data.length > 0;
    }

    public toggleAllRows() {
        if (this.isAllSelected()) { this.selection.clear(); return; }
        this.selection.select(...this._dataSource.data);
    }

    // ─── Exclusão individual ───
    public abrirModalExcluir(item: CentroCusto): void {
        const dialogRef = this.dialog.open(ConfirmModalComponent, {
            data: {
                title: 'Confirmar exclusão',
                description: `Confirma a exclusão do centro de custo '${item.nome}'?`,
                buttonConfirm: 'Confirmar',
                buttonClose: 'Cancelar'
            }
        });

        dialogRef.afterClosed().subscribe(result => {
            if (result?.confirm) {
                this._isLoading = true;
                this.centroCustoService.delete(item.idCentroCusto).subscribe({
                    next: (response: DeleteCentroCustoResponse) => {
                        this._isLoading = false;
                        if (response.isError && response.possuiMovimentacoes) {
                            this.abrirModalMovimentacoes(item);
                        } else if (response.isError) {
                            this.alertService.show('Erro', response.errorDescription || 'Erro ao excluir', AlertType.error);
                        } else {
                            this.alertService.show('Sucesso', 'Centro de custo excluído com sucesso', AlertType.success);
                            this.clickPesquisar();
                        }
                    },
                    error: (err) => {
                        this._isLoading = false;
                        this.alertService.show('Erro inesperado', err.message || 'Erro ao excluir', AlertType.error);
                    }
                });
            }
        });
    }

    // ─── Modal de movimentações (oferece inativar) ───
    private abrirModalMovimentacoes(item: CentroCusto): void {
        const dialogRef = this.dialog.open(ConfirmModalComponent, {
            data: {
                title: 'Exclusão não permitida',
                description: `O centro de custo "${item.nome}" possui lançamentos vinculados, por este motivo não é possível fazer sua exclusão. Deseja deixá-lo como inativo?`,
                buttonConfirm: 'Inativar centro de custo',
                buttonClose: 'Cancelar'
            }
        });

        dialogRef.afterClosed().subscribe(result => {
            if (result?.confirm) {
                this._isLoading = true;
                this.centroCustoService.inativar(item.idCentroCusto).subscribe({
                    next: (res) => {
                        this._isLoading = false;
                        if (res.isError) {
                            this.alertService.show('Erro', res.errorDescription || 'Erro ao inativar', AlertType.error);
                        } else {
                            this.alertService.show('Sucesso', 'Centro de custo inativado com sucesso', AlertType.success);
                            this.clickPesquisar();
                        }
                    },
                    error: (err) => {
                        this._isLoading = false;
                        this.alertService.show('Erro inesperado', err.message || 'Erro ao inativar', AlertType.error);
                    }
                });
            }
        });
    }

    // ─── Inativação individual via menu ───
    public inativarItem(item: CentroCusto): void {
        this._isLoading = true;
        this.centroCustoService.inativar(item.idCentroCusto).subscribe({
            next: (res) => {
                this._isLoading = false;
                if (res.isError) {
                    this.alertService.show('Erro', res.errorDescription || 'Erro ao inativar', AlertType.error);
                } else {
                    this.alertService.show('Sucesso', 'Centro de custo inativado com sucesso', AlertType.success);
                    this.clickPesquisar();
                }
            },
            error: (err) => {
                this._isLoading = false;
                this.alertService.show('Erro inesperado', err.message || 'Erro ao inativar', AlertType.error);
            }
        });
    }

    // ─── Ações em massa: Inativar ───
    public bulkInativar(): void {
        const itemsToInactivate = this.selection.selected;
        if (itemsToInactivate.length === 0) return;

        const dialogRef = this.dialog.open(ConfirmModalComponent, {
            data: {
                title: 'Confirmar inativação',
                description: `Deseja inativar ${itemsToInactivate.length} centro(s) de custo selecionado(s)?`,
                buttonConfirm: 'Inativar',
                buttonClose: 'Cancelar'
            }
        });

        dialogRef.afterClosed().subscribe(result => {
            if (result?.confirm) {
                this._isLoading = true;
                const ids = itemsToInactivate.map(i => i.idCentroCusto);
                this.centroCustoService.bulkInativar(ids).subscribe({
                    next: (response) => {
                        this._isLoading = false;
                        if (response.isError) {
                            this.alertService.show('Erro', response.errorDescription || 'Erro ao inativar', AlertType.error);
                        } else {
                            this.alertService.show('Sucesso', 'Centro(s) de custo inativado(s) com sucesso!', AlertType.success);
                            this.selection.clear();
                            this.clickPesquisar();
                        }
                    },
                    error: (err) => {
                        this._isLoading = false;
                        this.alertService.show('Erro inesperado', err.message || 'Erro ao inativar', AlertType.error);
                    }
                });
            }
        });
    }

    // ─── Ações em massa: Excluir ───
    public bulkExcluir(): void {
        const itemsToDelete = this.selection.selected;
        if (itemsToDelete.length === 0) return;

        const dialogRef = this.dialog.open(ConfirmModalComponent, {
            data: {
                title: 'Confirmar exclusão',
                description: `Deseja excluir ${itemsToDelete.length} centro(s) de custo selecionado(s)?`,
                buttonConfirm: 'Confirmar',
                buttonClose: 'Cancelar'
            }
        });

        dialogRef.afterClosed().subscribe(result => {
            if (result?.confirm) {
                this._isLoading = true;
                const ids = itemsToDelete.map(i => i.idCentroCusto);
                this.centroCustoService.bulkDelete(ids).subscribe({
                    next: (response: BulkDeleteCentroCustoResponse) => {
                        this._isLoading = false;
                        if (response.isError) {
                            this.alertService.show('Erro', response.errorDescription || 'Erro ao excluir', AlertType.error);
                            return;
                        }

                        const bloqueados = response.resultados.filter(r => r.status === StatusExclusaoCentroCustoEnum.BloqueadoMovimentacao);

                        if (bloqueados.length > 0 && response.totalExcluidos > 0) {
                            // Alguns excluídos, alguns bloqueados
                            const nomes = bloqueados.map(b => b.nome).join(', ');
                            this.alertService.show('Atenção',
                                `${response.totalExcluidos} centro(s) excluído(s). ${bloqueados.length} centro(s) possuem lançamentos vinculados e não foram excluídos: ${nomes}. Deseja inativá-los?`,
                                AlertType.warning);
                            this.abrirModalBulkMovimentacoes(bloqueados.map(b => b.idCentroCusto), bloqueados.map(b => b.nome));
                        } else if (bloqueados.length > 0 && response.totalExcluidos === 0) {
                            // Todos bloqueados
                            const nomes = bloqueados.map(b => b.nome).join(', ');
                            this.abrirModalBulkMovimentacoes(bloqueados.map(b => b.idCentroCusto), bloqueados.map(b => b.nome));
                        } else {
                            this.alertService.show('Sucesso', `${response.totalExcluidos} centro(s) de custo excluído(s) com sucesso!`, AlertType.success);
                        }

                        this.selection.clear();
                        this.clickPesquisar();
                    },
                    error: (err) => {
                        this._isLoading = false;
                        this.alertService.show('Erro inesperado', err.message || 'Erro ao excluir', AlertType.error);
                    }
                });
            }
        });
    }

    private abrirModalBulkMovimentacoes(ids: number[], nomes: string[]): void {
        const nomesStr = nomes.join(', ');
        const dialogRef = this.dialog.open(ConfirmModalComponent, {
            data: {
                title: 'Centros de custo com lançamentos vinculados',
                description: `Os seguintes centros de custo possuem lançamentos vinculados e não podem ser excluídos: ${nomesStr}. Deseja inativá-los?`,
                buttonConfirm: 'Inativar',
                buttonClose: 'Cancelar'
            }
        });

        dialogRef.afterClosed().subscribe(result => {
            if (result?.confirm) {
                this._isLoading = true;
                this.centroCustoService.bulkInativar(ids).subscribe({
                    next: (response) => {
                        this._isLoading = false;
                        if (response.isError) {
                            this.alertService.show('Erro', response.errorDescription || 'Erro ao inativar', AlertType.error);
                        } else {
                            this.alertService.show('Sucesso', 'Centro(s) de custo inativado(s) com sucesso!', AlertType.success);
                            this.clickPesquisar();
                        }
                    },
                    error: (err) => {
                        this._isLoading = false;
                        this.alertService.show('Erro inesperado', err.message || 'Erro ao inativar', AlertType.error);
                    }
                });
            }
        });
    }
}
